import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';

/**
 * POI proximity filter URL params.
 * nearKey and nearValue identify the POI category (e.g. amenity/school).
 * nearRadius is the search radius in meters (number, to avoid JSON-encoding in URL).
 * nearPoiId is a specific POI UUID for per-POI proximity filtering (mutually exclusive with nearKey/nearValue).
 */
export interface PoiFilterParams {
  nearKey?: string;
  nearValue?: string;
  nearRadius?: number;
  nearPoiId?: string;
}

function parseNearRadius(raw: unknown): number | undefined {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function readPoiFilterParams(search: Record<string, unknown>): PoiFilterParams {
  const result: PoiFilterParams = {};
  const { nearKey, nearValue, nearRadius, nearPoiId } = search;
  if (typeof nearKey === 'string' && nearKey) result.nearKey = nearKey;
  if (typeof nearValue === 'string' && nearValue) result.nearValue = nearValue;
  if (typeof nearPoiId === 'string' && nearPoiId) result.nearPoiId = nearPoiId;
  const r = parseNearRadius(nearRadius);
  if (r !== undefined) result.nearRadius = r;
  return result;
}

function buildNextSearch(
  current: Record<string, unknown>,
  value: PoiFilterParams,
): Record<string, unknown> {
  const next = { ...current };

  if (value.nearPoiId) {
    next['nearPoiId'] = value.nearPoiId;
    delete next['nearKey'];
    delete next['nearValue'];
  } else {
    delete next['nearPoiId'];
    if (value.nearKey) {
      next['nearKey'] = value.nearKey;
    } else {
      delete next['nearKey'];
    }
    if (value.nearValue) {
      next['nearValue'] = value.nearValue;
    } else {
      delete next['nearValue'];
    }
  }

  if (value.nearRadius != null && value.nearRadius > 0) {
    next['nearRadius'] = value.nearRadius;
  } else {
    delete next['nearRadius'];
  }

  return next;
}

/** Returns true when enough POI filter params are set to constitute an active proximity filter. */
export function isPoiFilterActive(params: PoiFilterParams): boolean {
  return (
    Boolean(params.nearKey && params.nearValue && params.nearRadius) ||
    Boolean(params.nearPoiId && params.nearRadius)
  );
}

/**
 * Reads POI proximity filter params from URL search params and provides a setter
 * that writes changes back via navigate() with replace: true.
 *
 * @returns [params, setParams] - params object with nearKey/nearValue/nearRadius/nearPoiId,
 *   and a setter that merges changes with existing URL params
 */
export function usePoiFilterParams(): [PoiFilterParams, (value: PoiFilterParams) => void] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  const params = React.useMemo<PoiFilterParams>(() => readPoiFilterParams(search), [search]);

  const setParams = React.useCallback(
    (value: PoiFilterParams) => {
      // Functional search updater: reads LIVE state at commit time instead of a
      // captured snapshot (CU-86cataqt9 stale-snapshot clobbering race fix).
      // Cast is necessary: navigate() without `from` has no concrete search type.
      type AnyNavigate = (opts: {
        search: (prev: Record<string, unknown>) => Record<string, unknown>;
        replace: boolean;
      }) => void;
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextSearch(prev, value),
        replace: true,
      });
    },
    [navigate],
  );

  return [params, setParams];
}
